import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Style/UserDiary.css";
import Header from "../../Common/Header";
import Section from "../../Common/Section";
import Loading from "../../Common/Loading";
import SubHeader from "../../Common/SubHeader";

interface ActivityRecord {
    DateTime: string;
    strDateTime: string;
    Activity: string;
    Score: number;
    TimeTaken: number;
    strTimeTaken: string;
    Attempts: number;
    Level: string;
    Unit: string;
    Chapter: string;
    SelectedFrom: string;
    StartDateTime_Local: string;
    EndDateTime_Local: string;
    DicCode: string;
    UnitCode: string;
    ChapterCode: string;
    LsnTestCode: string;
    Type: number;
    HWID: number | null;
    TutSpentTime: number;
    SessionID: number;
    LessonDesc: string;
    LevelDesc: string;
    UnitDesc: string;
    ChapterDesc: string;
}

const UserDiary: React.FC = () => {
    const [sortBy, setSortBy] = useState("DateTime");
    const [sortOrder, setSortOrder] = useState("DESC");
    const [period, setPeriod] = useState("Daily");
    const [timeSpan, setTimeSpan] = useState(1);
    const [dateDisplay, setDateDisplay] = useState("");
    const [showNextDate, setShowNextDate] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [activities, setActivities] = useState<ActivityRecord[]>([]);
    const [showDates, setShowDates] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [currentSubject, setCurrentSubject] = useState<string>(
        sessionStorage.getItem("Subject") || "Mathematics"
    );
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [showBackToBottom, setShowBackToBottom] = useState(true);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const apiUrl = process.env.REACT_APP_DOCUMENT_LINK ;




    const findScrollContainer = (): HTMLElement => {
        const candidates: (HTMLElement | null)[] = [
            (document.scrollingElement as HTMLElement) || null,
            document.getElementById("contents_math"),
            document.getElementById("main_top_container"),
            document.getElementById("contents_english"),
            document.getElementById("contents_physics"),
            document.getElementById("contents_chemistry"),
            document.getElementById("contents_biology"),
            document.body,
            document.documentElement,
        ];

        for (const el of candidates) {
            if (!el) continue;
            try {
                const style = window.getComputedStyle(el);
                const overflowY = style.overflowY;
                if (el.scrollHeight > el.clientHeight && (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay" || overflowY === "visible")) {
                    return el;
                }
            } catch (e) {
            }
        }
        return (document.scrollingElement as HTMLElement) || document.documentElement;
    };

    // detect and store the scroll container so click handlers can scroll the same element
    scrollContainerRef.current = findScrollContainer();
    const registeredContainer = scrollContainerRef.current;

    const handleScroll = () => {
        const el = (scrollContainerRef.current || registeredContainer || (document.scrollingElement as HTMLElement) || document.documentElement) as HTMLElement;
        const scrollTop = el.scrollTop;
        const innerH = el.clientHeight || window.innerHeight;
        const scrollHeight = el.scrollHeight;

        const scrollable = Math.max(0, scrollHeight - innerH);
        const TOL = 6;
        const atTop = scrollTop <= TOL;
        const atBottom = scrollTop >= Math.max(0, scrollable - TOL);
        const ratio = scrollable <= 0 ? 0 : scrollTop / scrollable;

        if (atTop) {
            setShowBackToBottom(true);
            setShowBackToTop(false);
            return;
        }

        if (atBottom) {
            setShowBackToTop(true);
            setShowBackToBottom(false);
            return;
        }

        if (ratio < 0.5) {
            setShowBackToBottom(true);
            setShowBackToTop(false);
        } else {
            setShowBackToTop(true);
            setShowBackToBottom(false);
        }
    };
    try {
        if (registeredContainer) {
            registeredContainer.addEventListener("scroll", handleScroll, { passive: true });
        } else {
            window.addEventListener("scroll", handleScroll, { passive: true });
        }
    } catch (e) {
        window.addEventListener("scroll", handleScroll, { passive: true });
    }

    // run once to set initial state
    setTimeout(handleScroll, 0);
    useEffect(() => {
        const interval = setInterval(() => {
            const storedSubject = sessionStorage.getItem("Subject") || "Mathematics";
            if (storedSubject !== currentSubject) {
                setCurrentSubject(storedSubject);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [currentSubject]);

    useEffect(() => {
        window.document.body.style.backgroundColor = "white";
        return () => {
            window.document.body.style.backgroundColor = "white";
        };
    }, []);

    useEffect(() => {
        const today = new Date();
        setDateDisplay(formatDisplayDate(today, period));
    }, []);

    const formatDisplayDate = (date: Date, periodType: string): string => {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        switch (periodType) {
            case "Daily":
                return `${dd}/${mm}/${yyyy}`;
            case "Weekly":
                const endOfWeek = new Date(date);
                const daysUntilSunday = (7 - endOfWeek.getDay()) % 7;
                endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday);
                return `Week ending ${String(endOfWeek.getDate()).padStart(2, '0')}/${String(endOfWeek.getMonth() + 1).padStart(2, '0')}/${endOfWeek.getFullYear()}`;
            case "Monthly":
                return `${date.toLocaleString('default', { month: 'long' })}, ${date.getFullYear()}`;
            default:
                return `${dd}/${mm}/${yyyy}`;
        }
    };

    const getDateRange = () => {
        const today = new Date();
        let startDate: Date, endDate: Date;
        switch (period) {
            case "Daily":
                endDate = new Date(today);
                endDate.setDate(today.getDate() + timeSpan - 1);
                startDate = new Date(endDate);
                break;
            case "Weekly":
                endDate = new Date(today);
                const daysUntilSunday = 7 - today.getDay();
                endDate.setDate(today.getDate() + daysUntilSunday + (7 * (timeSpan - 1)));
                startDate = new Date(endDate);
                startDate.setDate(endDate.getDate() - 6);
                break;
            case "Monthly":
                startDate = new Date(today.getFullYear(), today.getMonth() + timeSpan - 1, 1);
                endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
                break;
            case "Between":
                if (fromDate && toDate) {
                    startDate = new Date(fromDate);
                    endDate = new Date(toDate);
                } else {
                    startDate = today;
                    endDate = today;
                }
                break;
            default:
                startDate = today;
                endDate = today;
        }
        return { fromDate: startDate, toDate: endDate };
    };

    const fetchActivities = useCallback(() => {
        setLoading(true);
        setError("");
        const { fromDate: start, toDate: end } = getDateRange();
        const startDateStr = start.toISOString().split('T')[0];
        const endDateStr = end.toISOString().split('T')[0];
        const subjectToSend = currentSubject === "Maths" ? "Mathematics" : currentSubject;
        fetch(`${apiUrl}Report/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                Login: sessionStorage.getItem("Login"),
                FamilyCode: sessionStorage.getItem("FamilyCode"),
                SchoolCode: sessionStorage.getItem("SchoolCode"),
                Subject: subjectToSend,
                StartDateTime: startDateStr,
                EndDateTime: endDateStr
            }),
        })
            .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
            })
            .then((data: ActivityRecord[]) => {
                setActivities(data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching activities:", error);
                setError("Failed to fetch activities");
                setActivities([]);
                setLoading(false);
            });
    }, [period, timeSpan, toDate, currentSubject, apiUrl, fromDate]);

    useEffect(() => {
        if (period !== "Between") {
            fetchActivities();
        }
    }, [period, timeSpan, currentSubject, fetchActivities]);

    useEffect(() => {
        if (period === "Between" && fromDate && toDate) {
            fetchActivities();
        }
    }, [period, fromDate, toDate, currentSubject, fetchActivities]);

    const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPeriod(value);
        setTimeSpan(1);
        setShowNextDate(false);
        if (value === "Between") {
            setShowDates(true);
            setFromDate("");
            setToDate("");
        } else {
            setShowDates(false);
            setFromDate("");
            setToDate("");
            const today = new Date();
            const formattedDate = formatDisplayDate(today, value);
            setDateDisplay(formattedDate);
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        if (id === "txtCalFromDate") {
            setFromDate(value);
            if (toDate && value) validateAndFetchDates(value, toDate);
        } else if (id === "txtCalToDate") {
            setToDate(value);
            if (fromDate && value) validateAndFetchDates(fromDate, value);
        }
    };

    const validateAndFetchDates = (from: string, to: string) => {
        if (!from || !to) {
            alert("Please select both From Date and To Date.");
            return;
        }
        const fromDateObj = new Date(from);
        const toDateObj = new Date(to);
        const lastWeekEndDate = new Date(2016, 6, 2);
        if (fromDateObj < lastWeekEndDate) {
            const yyyy = lastWeekEndDate.getFullYear();
            const mm = String(lastWeekEndDate.getMonth() + 1).padStart(2, '0');
            const dd = String(lastWeekEndDate.getDate()).padStart(2, '0');
            setFromDate(`${yyyy}-${mm}-${dd}`);
        }
    };

    const handleSort = (column: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (sortBy === column) {
            setSortOrder(prevOrder => prevOrder === "ASC" ? "DESC" : "ASC");
        } else {
            setSortBy(column);
            setSortOrder("ASC");
        }
    };

    const handlePrevDate = () => {
        const newTimeSpan = timeSpan - 1;
        setTimeSpan(newTimeSpan);
        setShowNextDate(true);
        const today = new Date();
        let displayDate: Date;
        switch (period) {
            case "Daily":
                displayDate = new Date(today);
                displayDate.setDate(today.getDate() + newTimeSpan - 1);
                break;
            case "Weekly":
                displayDate = new Date(today);
                const daysUntilSunday = 7 - today.getDay();
                displayDate.setDate(today.getDate() + daysUntilSunday + (7 * (newTimeSpan - 1)));
                break;
            case "Monthly":
                displayDate = new Date(today.getFullYear(), today.getMonth() + newTimeSpan - 1, 1);
                break;
            default:
                displayDate = today;
        }
        setDateDisplay(formatDisplayDate(displayDate, period));
    };

    const handleNextDate = () => {
        if (timeSpan < 1) {
            const newTimeSpan = timeSpan + 1;
            setTimeSpan(newTimeSpan);
            if (newTimeSpan === 1) {
                setShowNextDate(false);
            }
            const today = new Date();
            let displayDate: Date;
            switch (period) {
                case "Daily":
                    displayDate = new Date(today);
                    displayDate.setDate(today.getDate() + newTimeSpan - 1);
                    break;
                case "Weekly":
                    displayDate = new Date(today);
                    const daysUntilSunday = 7 - today.getDay();
                    displayDate.setDate(today.getDate() + daysUntilSunday + (7 * (newTimeSpan - 1)));
                    break;
                case "Monthly":
                    displayDate = new Date(today.getFullYear(), today.getMonth() + newTimeSpan - 1, 1);
                    break;
                default:
                    displayDate = today;
            }
            setDateDisplay(formatDisplayDate(displayDate, period));
        }
    };

    const sortedActivities = [...activities].sort((a, b) => {
        const isAsc = sortOrder === "ASC";
        const diff = (() => {
            switch (sortBy) {
                case "DateTime":
                    return new Date(a.EndDateTime_Local || a.DateTime).getTime() -
                        new Date(b.EndDateTime_Local || b.DateTime).getTime();
                case "Score": return a.Score - b.Score;
                case "TimeTaken": return a.TimeTaken - b.TimeTaken;
                case "Attempts": return a.Attempts - b.Attempts;
                case "Level": return a.Level.localeCompare(b.Level);
                case "Unit": return a.Unit.localeCompare(b.Unit);
                case "Chapter": return a.Chapter.localeCompare(b.Chapter);
                case "SelectedFrom": return a.SelectedFrom.localeCompare(b.SelectedFrom);
                default: return 0;
            }
        })();
        return isAsc ? diff : -diff;
    });

    const getSortClass = (column: string): string => {
        if (sortBy === column) {
            return `sort ${sortOrder === "ASC" ? "sort-up" : "sort-down"}`;
        }
        return "sort sort-down";
    };

    // Native alert for refresh confirmation
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (period !== "Daily") {
                e.preventDefault();
                e.returnValue = "The page that you're looking for used information that you entered. Returning to that page might cause any action you took to be repeated. Do you want to continue?";
                return e.returnValue;
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [period]);

    return (
        <div style={{ backgroundColor: "white", maxHeight: "150%" }}>
            {loading && <Loading />}
            <form>
                {/* <button
                    type="button"
                    className="back-to-top"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                    Back to Top
                </button>
                <button
                    type="button"
                    className="back-to-bottom"
                    id="back_to_bottom_link"
                    onClick={() =>
                        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })
                    }
                >
                    Back to Bottom
                </button> */}

                <div id="main_top_container">
                    <Header />
                    <a
                        href="#bottom"
                        className="back-to-bottom"
                        onClick={(e) => {
                            e.preventDefault();
                            const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                            try {
                                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                            } catch {
                                window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
                            }
                            setTimeout(() => {
                                (container as any).scrollTop = (container as any).scrollHeight;
                                (document.body as any).scrollTop = document.body.scrollHeight;
                            }, 400);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                                try {
                                    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                                } catch {
                                    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
                                }
                                setTimeout(() => {
                                    (container as any).scrollTop = (container as any).scrollHeight;
                                    (document.body as any).scrollTop = document.body.scrollHeight;
                                }, 400);
                            }
                        }}
                        aria-label="Back to bottom"
                        role="button"
                        tabIndex={0}
                        style={{
                            opacity: showBackToBottom ? 1 : 0,
                            transform: showBackToBottom ? 'translateY(0)' : 'translateY(8px)',
                            display: 'block',
                            cursor: 'pointer',
                            zIndex: 2147483647,
                            pointerEvents: showBackToBottom ? 'auto' : 'none',
                            position: 'fixed',
                            left: 20,
                            bottom: 20,
                            transition: 'opacity 240ms ease, transform 240ms ease',


                            boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
                        }}
                    >
                        Back to Bottom
                    </a>

                    <a
                        href="#top"
                        className="back-to-top"
                        onClick={(e) => {
                            e.preventDefault();
                            const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                            try {
                                container.scrollTo({ top: 0, behavior: 'smooth' });
                            } catch {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                            setTimeout(() => {
                                (container as any).scrollTop = 0;
                                (document.body as any).scrollTop = 0;
                            }, 300);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                                try {
                                    container.scrollTo({ top: 0, behavior: 'smooth' });
                                } catch {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                                setTimeout(() => {
                                    (container as any).scrollTop = 0;
                                    (document.body as any).scrollTop = 0;
                                }, 300);
                            }
                        }}
                        aria-label="Back to top"
                        role="button"
                        tabIndex={0}
                        style={{
                            opacity: showBackToTop ? 1 : 0,
                            transform: showBackToTop ? 'translateY(0)' : 'translateY(8px)',
                            display: 'block',
                            cursor: 'pointer',
                            zIndex: 2147483647,
                            pointerEvents: showBackToTop ? 'auto' : 'none',
                            position: 'fixed',
                            right: 20,
                            bottom: 20,
                            transition: 'opacity 240ms ease, transform 240ms ease',
                            boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
                        }}
                    >
                        Back to Top
                    </a>
                    <Section />
                </div>

                <SubHeader isMSRPage={true} pageType="userdiary">
                    <div className="section-container" id="studentreportscollapse">
                        <div className="content-container">
                            <div className="content-top" style={{ backgroundColor: "white", maxHeight: "100vh" }}>
                                <div className="report-heading-container">
                                    <div className="report-heading">User Diary</div>
                                </div>

                                <div className="report-displayed-select">
                                    <div className="report-displayed-heading">Report Displayed:</div>
                                    <br />
                                    <div className="radio-mobile-container">
                                        {["Daily", "Weekly", "Monthly"].map((periodValue) => (
                                            <div className="radio-container" key={periodValue}>
                                                <input
                                                    type="radio"
                                                    id={`rdo${periodValue}`}
                                                    name="toggler"
                                                    value={periodValue}
                                                    checked={period === periodValue}
                                                    onChange={handlePeriodChange}
                                                />
                                                <label htmlFor={`rdo${periodValue}`}>{periodValue}</label>
                                            </div>
                                        ))}
                                        <div className="radio-container">
                                            <input
                                                type="radio"
                                                id="rdoViewFrom"
                                                name="toggler"
                                                value="Between"
                                                checked={period === "Between"}
                                                onChange={handlePeriodChange}
                                            />
                                            <label htmlFor="rdoViewFrom">View From:</label>
                                            <input
                                                type="date"
                                                id="txtCalFromDate"
                                                value={fromDate}
                                                onChange={handleDateChange}
                                                disabled={!showDates}
                                                max={new Date().toISOString().split("T")[0]}
                                            />
                                            To:
                                            <input
                                                type="date"
                                                id="txtCalToDate"
                                                value={toDate}
                                                onChange={handleDateChange}
                                                disabled={!showDates}
                                            />
                                        </div>
                                    </div>

                                    {period !== "Between" && (
                                        <div className="week-ending-container">
                                            <button
                                                type="button"
                                                className="arrow-left"
                                                onClick={handlePrevDate}
                                                aria-label="Previous date"
                                                style={{ cursor: "pointer" }}
                                            />
                                            <div className="wk-ending-text" id="divDateDisplay">
                                                {dateDisplay}
                                            </div>
                                            {showNextDate && (
                                                <button
                                                    type="button"
                                                    className="arrow-right"
                                                    onClick={handleNextDate}
                                                    aria-label="Next date"
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="widget-container">
                                    <div className="table-mobile-scroll">
                                        ← &nbsp;Scroll table horizontally to view more&nbsp; →
                                    </div>
                                    <div className="scrolling-table-container">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        Date/Time
                                                        <button
                                                            className={getSortClass("DateTime")}
                                                            onClick={(event) => handleSort("DateTime", event)}
                                                        ></button>
                                                    </th>
                                                    <th className="activity-max-length">Activity</th>
                                                    <th>
                                                        Score
                                                        <button
                                                            className={getSortClass("Score")}
                                                            onClick={(event) => handleSort("Score", event)}
                                                        ></button>
                                                    </th>
                                                    <th>
                                                        <div className="th-double-line">
                                                            Time Taken<br />(mins)
                                                        </div>
                                                        <button
                                                            className={getSortClass("TimeTaken")}
                                                            onClick={(event) => handleSort("TimeTaken", event)}
                                                        ></button>
                                                    </th>
                                                    {/* <th>
                                                        <div className="th-double-line">
                                                            No. of<br />attempts
                                                        </div>
                                                        <button
                                                            className={getSortClass("Attempts")}
                                                            onClick={(event) => handleSort("Attempts", event)}
                                                        ></button>
                                                    </th> */}
                                                    {/* <th>
                                                        Level
                                                        <button
                                                            className={getSortClass("Level")}
                                                            onClick={(event) => handleSort("Level", event)}
                                                        ></button>
                                                    </th> */}
                                                    <th>
                                                        Unit
                                                        <button
                                                            className={getSortClass("Unit")}
                                                            onClick={(event) => handleSort("Unit", event)}
                                                        ></button>
                                                    </th>
                                                    <th>
                                                        Chapter
                                                        <button
                                                            className={getSortClass("Chapter")}
                                                            onClick={(event) => handleSort("Chapter", event)}
                                                        ></button>
                                                    </th>
                                                    <th>
                                                        Selected From
                                                        <button
                                                            className={getSortClass("SelectedFrom")}
                                                            onClick={(event) => handleSort("SelectedFrom", event)}
                                                        ></button>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {error && (
                                                    <tr>
                                                        <td colSpan={9} style={{ textAlign: 'center', color: 'red' }}>
                                                            {error}
                                                        </td>
                                                    </tr>
                                                )}
                                                {!loading && sortedActivities.length === 0 && !error ? (
                                                    <tr>
                                                        <td colSpan={9} style={{ textAlign: 'center' }}>
                                                            No activities found for the selected period
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    sortedActivities.map((activity, index) => (
                                                        <tr key={`${activity.LsnTestCode}-${index}`}>
                                                            <td style={{whiteSpace:'nowrap'}}>{activity.DateTime}</td>
                                                            <td>{activity.Activity}</td>
                                                            <td>{activity.Score}</td>
                                                            <td>{activity.strTimeTaken}</td>
                                                            {/* <td>{activity.Attempts}</td> */}
                                                            {/* <td className="td-max-length">{activity.Level}</td> */}
                                                            <td className="td-max-length">{activity.Unit}</td>
                                                            <td className="td-max-length">{activity.Chapter}</td>
                                                            <td>{activity.SelectedFrom}</td>
                                                        </tr>

                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </SubHeader>
                <input type="hidden" id="hidUserDiarySortBy" value={sortBy} />
                <input type="hidden" id="hidUserDiarySortOrder" value={sortOrder} />
                <input type="hidden" id="hidOverAllWeeklyProgress" value="0" />
                <input type="hidden" id="hidTimeSpan" value={timeSpan} />
                <input type="hidden" id="hidSchoolCode" value={sessionStorage.getItem("SchoolCode") || ""} />
                <input type="hidden" id="hidFamilyCode" value={sessionStorage.getItem("FamilyCode") || ""} />
            </form>
        </div>
    );
};

export default UserDiary;
